/**
 * Iter53 P47 — Detalii vânzare tab extracted from SaleForm.js.
 *
 * Consumes SaleFormContext with the extended `details` bag so JSX below can
 * reference the same identifiers used in the original inline version (zero JSX changes).
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, UserPlus, Loader2, ChevronsUpDown, Check, PenLine } from "lucide-react";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { cn } from "../../lib/utils";
import { useSaleFormContext } from "./SaleFormContext";

export const SaleFormDetailsTab = () => {
  const { formData, handleChange, isEdit, details } = useSaleFormContext();
  const {
    selectedClient, setSelectedClient,
    showNewClient, setShowNewClient,
    clientSearchOpen, setClientSearchOpen,
    clientSearchTerm, setClientSearchTerm,
    clientSearchResults,
    searchingClients,
    duplicates,
    handleSelectClient,
    getClientDisplayName,
    suppliers,
    salesChannels,
    statuses,
    getDistributionZonesForCommodity,
    isAdminOrBO,
    commercialAgents,
    offers = [],
  } = details;
  const isLink = !isEdit && String(formData.identity_resolution || "").startsWith("link");

  const [signerPickerOpen, setSignerPickerOpen] = useState(false);
  const selectedSigner = (commercialAgents || []).find((a) => a.id === formData.commercial_agent_id);

  return (
    <>
        {/* Client Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Date Client</span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={!showNewClient ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowNewClient(false)}
                  data-testid="search-client-btn"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Caută Client
                </Button>
                <Button
                  type="button"
                  variant={showNewClient ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setShowNewClient(true);
                    setSelectedClient(null);
                  }}
                  data-testid="new-client-btn"
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Client Nou
                </Button>
              </div>
            </CardTitle>
            <CardDescription>
              {showNewClient ? "Introdu datele pentru un client nou" : "Caută un client existent"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Client Search */}
            {!showNewClient && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Caută Client</Label>
                  <Popover open={clientSearchOpen} onOpenChange={setClientSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={clientSearchOpen}
                        className="w-full justify-between"
                        data-testid="client-search-trigger"
                      >
                        {selectedClient ? getClientDisplayName(selectedClient) : "Selectează client..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-full p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder="Caută după nume, CNP, CUI..."
                          value={clientSearchTerm}
                          onValueChange={setClientSearchTerm}
                          data-testid="client-search-input"
                        />
                        <CommandList>
                          <CommandEmpty>
                            {searchingClients ? (
                              <div className="flex items-center justify-center py-2">
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Se caută...
                              </div>
                            ) : clientSearchTerm.length < 2 ? (
                              "Introdu minim 2 caractere"
                            ) : (
                              "Nu s-au găsit clienți"
                            )}
                          </CommandEmpty>
                          <CommandGroup>
                            {clientSearchResults.map((client) => (
                              <CommandItem
                                key={client.id}
                                value={client.id}
                                onSelect={() => handleSelectClient(client)}
                                data-testid={`client-option-${client.id}`}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    selectedClient?.id === client.id ? "opacity-100" : "opacity-0"
                                  )}
                                />
                                <div className="flex flex-col">
                                  <span className="font-medium">{getClientDisplayName(client)}</span>
                                  <span className="text-xs text-slate-500">
                                    {client.client_type === "PF" ? client.cnp : client.cui} | {client.county}, {client.locality}
                                  </span>
                                </div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                
                {selectedClient && !isEdit && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium text-slate-900">{getClientDisplayName(selectedClient)}</p>
                    <p className="text-sm text-slate-500">
                      {selectedClient.client_type === "PF" ? `CNP: ${selectedClient.cnp}` : `CUI: ${selectedClient.cui}`}
                    </p>
                    <p className="text-sm text-slate-500">
                      {selectedClient.county}, {selectedClient.locality}, {selectedClient.street}
                    </p>
                    {selectedClient.phone_mobile && (
                      <p className="text-sm text-slate-500">Tel: {selectedClient.phone_mobile}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* New Client Form */}
            {(showNewClient || (isEdit && selectedClient)) && (
              <>
                {isEdit && selectedClient && !showNewClient && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 flex items-center justify-between" data-testid="client-edit-banner">
                    <span>
                      Editezi clientul existent: <strong>{getClientDisplayName(selectedClient)}</strong>. CNP/CUI nu pot fi modificate aici.
                    </span>
                    <Link to={`/clients/${selectedClient.id}`} className="text-blue-700 underline hover:text-blue-900 ml-3 whitespace-nowrap">
                      Vezi pagina clientului →
                    </Link>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Tip Client</Label>
                    <Select
                      value={formData.client_type}
                      onValueChange={(value) => handleChange("client_type", value)}
                      disabled={isEdit && !!selectedClient}
                    >
                      <SelectTrigger data-testid="client_type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PF">Persoană Fizică (PF)</SelectItem>
                        <SelectItem value="PJ">Persoană Juridică (PJ)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {formData.client_type === "PF" ? (
                    <>
                      <div className="space-y-2">
                        <Label>CNP *</Label>
                        <Input
                          placeholder="CNP"
                          value={formData.cnp}
                          onChange={(e) => handleChange("cnp", e.target.value)}
                          disabled={isEdit && !!selectedClient}
                          data-testid="cnp"
                        />
                        {duplicates.cnp_cui && duplicates.cnp_cui.length > 0 && formData.cnp && (
                          <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mt-1" data-testid="duplicate-cnp-warning">
                            <p className="font-medium text-amber-800">
                              ⚠ CNP există deja în {duplicates.cnp_cui.length} client{duplicates.cnp_cui.length > 1 ? "i" : ""}:
                            </p>
                            <ul className="mt-1 space-y-0.5">
                              {duplicates.cnp_cui.slice(0, 3).map((d) => (
                                <li key={d.client_id} className="text-amber-700">{d.name} · {d.contracts_count} contract{d.contracts_count !== 1 ? "e" : ""}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>Nume</Label>
                        <Input
                          placeholder="Nume"
                          value={formData.last_name}
                          onChange={(e) => handleChange("last_name", e.target.value)}
                          data-testid="last_name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Prenume</Label>
                        <Input
                          placeholder="Prenume"
                          value={formData.first_name}
                          onChange={(e) => handleChange("first_name", e.target.value)}
                          data-testid="first_name"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>CUI *</Label>
                        <Input
                          placeholder="CUI"
                          value={formData.cui}
                          onChange={(e) => handleChange("cui", e.target.value)}
                          disabled={isEdit && !!selectedClient}
                          data-testid="cui"
                        />
                        {duplicates.cnp_cui && duplicates.cnp_cui.length > 0 && formData.cui && (
                          <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mt-1" data-testid="duplicate-cui-warning">
                            <p className="font-medium text-amber-800">
                              ⚠ CUI există deja la {duplicates.cnp_cui.length} firm{duplicates.cnp_cui.length > 1 ? "e" : "ă"}:
                            </p>
                            <ul className="mt-1 space-y-0.5">
                              {duplicates.cnp_cui.slice(0, 3).map((d) => (
                                <li key={d.client_id} className="text-amber-700">{d.name} · {d.contracts_count} contract{d.contracts_count !== 1 ? "e" : ""}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label>Denumire Firmă</Label>
                        <Input
                          placeholder="Denumire firmă"
                          value={formData.company_name}
                          onChange={(e) => handleChange("company_name", e.target.value)}
                          data-testid="company_name"
                        />
                      </div>
                    </>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <Label>Județ *</Label>
                    <Input
                      placeholder="Județul"
                      value={formData.county}
                      onChange={(e) => handleChange("county", e.target.value)}
                      data-testid="county"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Localitate *</Label>
                    <Input
                      placeholder="Localitatea"
                      value={formData.locality}
                      onChange={(e) => handleChange("locality", e.target.value)}
                      data-testid="locality"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stradă *</Label>
                    <Input
                      placeholder="Strada și numărul"
                      value={formData.street}
                      onChange={(e) => handleChange("street", e.target.value)}
                      data-testid="street"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Telefon Mobil</Label>
                    <Input
                      placeholder="07xxxxxxxx"
                      value={formData.phone_mobile}
                      onChange={(e) => handleChange("phone_mobile", e.target.value)}
                      data-testid="phone_mobile"
                    />
                    {duplicates.phone && duplicates.phone.length > 0 && (
                      <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mt-1" data-testid="duplicate-phone-warning">
                        <p className="font-medium text-amber-800">
                          ⚠ Telefonul este folosit și de {duplicates.phone.length} client{duplicates.phone.length > 1 ? "i" : ""}:
                        </p>
                        <ul className="mt-1 space-y-0.5">
                          {duplicates.phone.slice(0, 3).map((d) => (
                            <li key={d.client_id} className="text-amber-700">
                              {d.name} · {d.contracts_count} contract{d.contracts_count !== 1 ? "e" : ""}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      placeholder="email@exemplu.ro"
                      value={formData.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      data-testid="email"
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Contract Info */}
        <Card>
          <CardHeader>
            <CardTitle>Informații Contract</CardTitle>
            <CardDescription>Date despre punct de consum și produs</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Tip Energie</Label>
              <Select
                value={formData.commodity}
                onValueChange={(value) => handleChange("commodity", value)}
              >
                <SelectTrigger data-testid="commodity">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="electric">Energie Electrică</SelectItem>
                  <SelectItem value="gas">Gaz Natural</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {!isEdit && (
              <div className="space-y-2" data-testid="offer-section">
                <Label>Ofertă {!isLink && <span className="text-rose-600">*</span>}</Label>
                {isLink ? (
                  <p className="text-xs text-slate-500 rounded border border-slate-200 p-2" data-testid="offer-reuse-note">
                    Reutilizare contract existent: oferta și tipul produsului contractului NU se modifică din acest formular.
                  </p>
                ) : (
                  <>
                    <Select value={formData.offer_id || "none"} onValueChange={(v) => handleChange("offer_id", v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="offer_id"><SelectValue placeholder="Selectează oferta" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Neselectat</SelectItem>
                        {offers.map((o) => (
                          <SelectItem key={o.id} value={o.id} data-testid={`offer-option-${o.id}`}>{o.name}{o.product_kind ? ` · ${o.product_kind}` : " · fără tip produs"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500" data-testid="product_kind_display">Tip produs: {formData.product_kind || "— (din ofertă)"}</p>
                  </>
                )}
              </div>
            )}
            <div className="space-y-2">
              <Label>Produs</Label>
              <Input
                placeholder="Denumire produs"
                value={formData.product_name}
                onChange={(e) => handleChange("product_name", e.target.value)}
                data-testid="product_name"
              />
            </div>

            <div className="space-y-2">
              <Label>POD</Label>
              <Input
                placeholder="Punct de consum"
                value={formData.pod}
                onChange={(e) => handleChange("pod", e.target.value)}
                data-testid="pod"
              />
              {duplicates.pod && duplicates.pod.length > 0 && (
                <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 mt-1" data-testid="duplicate-pod-warning">
                  <p className="font-medium text-amber-800 flex items-center gap-1">
                    ⚠ POD-ul este folosit și în {duplicates.pod.length} contract{duplicates.pod.length > 1 ? "e" : ""}:
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {duplicates.pod.slice(0, 3).map((d) => (
                      <li key={d.contract_id}>
                        <Link to={`/sales?search=${encodeURIComponent(d.pod)}`} className="text-amber-700 underline hover:text-amber-900">
                          {d.contract_number || d.contract_id.slice(0, 8)} · {d.client} · semnat {d.signed_date || "—"}
                        </Link>
                      </li>
                    ))}
                  </ul>
                  {!isEdit && (
                    <div className="mt-2 space-y-1 border-t border-amber-200 pt-2" data-testid="pod-identity-resolution">
                      <p className="font-medium text-amber-900">Regula de identitate: POD-ul identifică locul de consum, nu contractul. Alege înainte de salvare:</p>
                      {duplicates.pod.slice(0, 5).map((d) => (
                        <div key={`link-${d.contract_id}`} className="space-y-1">
                          <label className="flex items-center gap-2">
                            <input type="radio" name="identity_resolution" checked={formData.identity_resolution === `link:${d.contract_id}`}
                              onChange={() => handleChange("identity_resolution", `link:${d.contract_id}`)} data-testid={`identity-link-${d.contract_id}`} />
                            <span>Aceeași vânzare/contract (reutilizează vânzarea existentă): {d.contract_number || d.contract_id.slice(0, 8)} · {d.client} · semnat {d.signed_date || "—"}</span>
                          </label>
                          <label className="flex items-center gap-2 pl-5">
                            <input type="radio" name="identity_resolution" checked={formData.identity_resolution === `link_new_sale:${d.contract_id}`}
                              onChange={() => handleChange("identity_resolution", `link_new_sale:${d.contract_id}`)} data-testid={`identity-link-new-sale-${d.contract_id}`} />
                            <span>Operațiune comercială distinctă pe acest contract (vânzare nouă, cu scop și justificare)</span>
                          </label>
                          {formData.identity_resolution === `link_new_sale:${d.contract_id}` && (
                            <div className="pl-5 space-y-1" data-testid="identity-new-sale-details">
                              <Input placeholder="Scopul operațiunii (ex. upsell VAS, prelungire, ofertă suplimentară)" value={formData.new_sale_purpose || ""}
                                onChange={(e) => handleChange("new_sale_purpose", e.target.value)} data-testid="identity-new-sale-purpose" />
                              <Textarea placeholder="Justificare (min. 10 caractere) — nu autorizează comision suplimentar" value={formData.justification || ""}
                                onChange={(e) => handleChange("justification", e.target.value)} data-testid="identity-new-sale-justification" />
                              <p className="text-[11px] text-slate-500" data-testid="identity-operation-key">cheie operațiune: {formData.operation_key || "—"} (păstrată la retry)</p>
                            </div>
                          )}
                        </div>
                      ))}
                      <label className="flex items-center gap-2">
                        <input type="radio" name="identity_resolution" checked={formData.identity_resolution === "new"} onChange={() => handleChange("identity_resolution", "new")} data-testid="identity-new" />
                        <span>Contract nou dovedit (completează ID Easy / nr. SF / nr. contract)</span>
                      </label>
                      {formData.identity_resolution === "new" && (
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="ID Easy (a36cj…)" value={formData.easy_contract_id || ""} onChange={(e) => handleChange("easy_contract_id", e.target.value)} data-testid="identity-easy-id" />
                          <Input placeholder="Nr. contract Salesforce" value={formData.sf_contract_id || ""} onChange={(e) => handleChange("sf_contract_id", e.target.value)} data-testid="identity-sf-id" />
                        </div>
                      )}
                      <label className="flex items-center gap-2">
                        <input type="radio" name="identity_resolution" checked={formData.identity_resolution === "new_needs_verification"}
                          onChange={() => handleChange("identity_resolution", "new_needs_verification")} data-testid="identity-new-needs-verification" />
                        <span>Dosar separat „Necesită verificare” (numărul extern lipsește încă; se rezolvă la sosirea ID-ului Easy/SF)</span>
                      </label>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>NLC</Label>
              <Input
                placeholder="NLC"
                value={formData.nlc}
                onChange={(e) => handleChange("nlc", e.target.value)}
                data-testid="nlc"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Zona Distribuție</Label>
              <Select
                value={formData.distribution_zone || "none"}
                onValueChange={(value) => handleChange("distribution_zone", value === "none" ? "" : value)}
              >
                <SelectTrigger data-testid="distribution_zone">
                  <SelectValue placeholder="Selectează zona" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Neselectat</SelectItem>
                  {getDistributionZonesForCommodity().map((zone) => (
                    <SelectItem key={zone} value={zone}>
                      {zone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Furnizor Anterior</Label>
              <Select
                value={formData.previous_supplier || "none"}
                onValueChange={(value) => handleChange("previous_supplier", value === "none" ? "" : value)}
              >
                <SelectTrigger data-testid="previous_supplier">
                  <SelectValue placeholder="Selectează furnizor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Neselectat</SelectItem>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={supplier.name}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Data Semnării</Label>
              <Input
                type="text"
                placeholder="DD.MM.YYYY"
                value={formData.signed_date}
                onChange={(e) => handleChange("signed_date", e.target.value)}
                data-testid="signed_date"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Consum Anual Estimat (kWh)</Label>
              <Input
                type="number"
                placeholder="Ex: 5000"
                value={formData.estimated_annual_kwh}
                onChange={(e) => handleChange("estimated_annual_kwh", e.target.value)}
                data-testid="estimated_annual_kwh"
              />
            </div>
          </CardContent>
        </Card>

        {/* Sale Info */}
        <Card>
          <CardHeader>
            <CardTitle>Informații Vânzare</CardTitle>
            <CardDescription>Date despre agent și procesare</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Canal Vânzare</Label>
              <Select
                value={formData.channel || "none"}
                onValueChange={(value) => handleChange("channel", value === "none" ? "" : value)}
              >
                <SelectTrigger data-testid="channel">
                  <SelectValue placeholder="Selectează canal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Neselectat</SelectItem>
                  {salesChannels.map((channel) => (
                    <SelectItem key={channel.id} value={channel.name}>
                      {channel.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={formData.internal_status}
                onValueChange={(value) => handleChange("internal_status", value)}
              >
                <SelectTrigger data-testid="internal_status">
                  <SelectValue placeholder="Selectează status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((status) => (
                    <SelectItem key={status.id} value={status.name}>
                      {status.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {!isEdit && isAdminOrBO && (
              <div className="space-y-2 md:col-span-2">
                <Label className="flex items-center gap-1">
                  <PenLine className="h-3.5 w-3.5 text-[#1e3a8a]" />
                  Agent real (semnatar)
                </Label>
                <Popover open={signerPickerOpen} onOpenChange={setSignerPickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between font-normal"
                      data-testid="commercial-signer-trigger"
                    >
                      {selectedSigner
                        ? `${selectedSigner.full_name}${selectedSigner.primary_marca_code ? ` [${selectedSigner.primary_marca_code}]` : ""}`
                        : "Selectează agentul care a semnat contractul..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[460px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Caută după nume sau marcă..." data-testid="signer-search-input" />
                      <CommandList className="max-h-64">
                        <CommandEmpty>Niciun agent găsit</CommandEmpty>
                        <CommandGroup>
                          {(commercialAgents || []).map((a) => (
                            <CommandItem
                              key={a.id}
                              value={`${a.full_name || ""} ${a.primary_marca_code || ""}`}
                              onSelect={() => {
                                handleChange("commercial_agent_id", a.id);
                                if (a.full_name) handleChange("agent_name", a.full_name);
                                if (a.primary_marca_code) handleChange("agent_code", a.primary_marca_code);
                                setSignerPickerOpen(false);
                              }}
                              data-testid={`signer-option-${a.id}`}
                            >
                              <Check className={cn("mr-2 h-4 w-4", formData.commercial_agent_id === a.id ? "opacity-100" : "opacity-0")} />
                              <span className="truncate">{a.full_name}</span>
                              {a.primary_marca_code && (
                                <span className="ml-2 font-mono text-xs text-slate-500">[{a.primary_marca_code}]</span>
                              )}
                              {a.agency_name && (
                                <span className="ml-2 text-xs text-slate-400">{a.agency_name}</span>
                              )}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500">
                  Agentul care a semnat efectiv contractul (poate diferi de cel raportat în Easy/SF).
                  Devine owner-ul comercial folosit în rapoarte și comisioane.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Nume Agent</Label>
              <Input
                placeholder="Ex: Ion Popescu"
                value={formData.agent_name}
                onChange={(e) => handleChange("agent_name", e.target.value)}
                disabled={!isAdminOrBO}
                data-testid="agent_name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Cod Agent</Label>
              <Input
                placeholder="Ex: AG001"
                value={formData.agent_code}
                onChange={(e) => handleChange("agent_code", e.target.value)}
                disabled={!isAdminOrBO}
                data-testid="agent_code"
              />
            </div>
            
            {isAdminOrBO && (
              <div className="space-y-2">
                <Label>Operator Backoffice</Label>
                <Input
                  placeholder="Ex: Maria Ionescu"
                  value={formData.bo_operator_name}
                  onChange={(e) => handleChange("bo_operator_name", e.target.value)}
                  data-testid="bo_operator_name"
                />
              </div>
            )}
            
            <div className="space-y-2 md:col-span-2">
              <Label>Note Agent</Label>
              <Textarea
                placeholder="Note adiționale..."
                value={formData.notes_agent}
                onChange={(e) => handleChange("notes_agent", e.target.value)}
                rows={3}
                data-testid="notes_agent"
              />
            </div>
          </CardContent>
        </Card>

    </>
  );
};
